// Ghép hồ sơ 360 vào trang chi tiết khách hàng: trả về các tab, huy hiệu phân loại cho thanh đầu, và các hộp thoại dùng chung
import React, { useState } from 'react';
import { Badge, ConfigProvider, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { CustomerDetailData } from '../../api/ChannelCustomer';
import { posTheme } from '../POS/components/PosShell';
import OrderDetail from '../POS/components/OrderDetail';
import type { Customer } from '../POS/data';
import usePosStore from '../POS/store';
import { useTreatmentAuth, useTreatmentSync } from '../Treatment/actions';
import { BookingModal, DeductModal, SellCardModal } from '../Treatment/modals';
import PayRequestModal from '../Treatment/PayRequestModal';
import { usePayRequestNotifier } from '../Treatment/payRequestNotifier';
import ClassPanel from './ClassPanel';
import type { AuditChange } from './data';
import HistoryPanel from './HistoryPanel';
import LedgerPanel from './LedgerPanel';
import Overview360 from './Overview360';
import PhotoPanel from './PhotoPanel';
import SignModal, { type SignTarget } from './SignModal';
import SignPanel, { DocViewer } from './SignPanel';
import useC360Store from './store';
import TreatmentPanel from './TreatmentPanel';
import VisitSignModal, { type VisitTarget } from './VisitSignModal';
import VisitsPanel from './VisitsPanel';
import { SegmentBadge, TierBadge, type C360Ops } from './ui360';
import useCustomer360 from './useCustomer360';

export const C360_TAB_KEYS = ['360', 'class', 'treatment', 'visits', 'photos', 'sign', 'ledger'];

export default function useCustomer360Tabs(customer: CustomerDetailData | null, opts: { appointments: React.ReactNode; setTab: (key: string) => void }) {
  useTreatmentSync();
  const c = useCustomer360(customer);
  const navigate = useNavigate();
  const { userName } = useTreatmentAuth();
  usePayRequestNotifier(() => opts.setTab('treatment'));

  const [sellOpen, setSellOpen] = useState(false);
  const [sellCustomer, setSellCustomer] = useState<Customer | null>(null);
  const [payId, setPayId] = useState<string | null>(null);
  const [deductId, setDeductId] = useState<string | null>(null);
  const [bookId, setBookId] = useState<string | null>(null);
  const [signTarget, setSignTarget] = useState<SignTarget | null>(null);
  const [visitTarget, setVisitTarget] = useState<VisitTarget | null>(null);
  const [docId, setDocId] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [uploadReq, setUploadReq] = useState<{ cardId?: string; usageId?: string; n: number } | null>(null);

  const cardOf = (id: string | null) => (id ? c.data.cards.find(x => x.id === id) || null : null);

  /** Bán thẻ cho khách chưa có hồ sơ bán hàng → tạo hồ sơ POS từ thông tin CMS và liên kết */
  const sell = () => {
    let pos = c.pos[0] || null;
    if (!pos && customer) {
      if (!c.party.phone) { message.warning('Khách chưa có số điện thoại – cập nhật SĐT trước khi bán thẻ'); return; }
      pos = usePosStore.getState().addCustomer({ name: c.party.name, phone: c.party.phone.replace(/\D/g, ''), source: `CMS #${customer.id}` });
      useC360Store.getState().link(c.key, pos.id, userName);
    }
    setSellCustomer(pos);
    setSellOpen(true);
  };

  const ops: C360Ops = {
    goTab: opts.setTab,
    pay: setPayId,
    deduct: setDeductId,
    book: setBookId,
    sign: setSignTarget,
    signVisit: date => setVisitTarget({ posIds: c.posIds, customerKey: c.key, date, party: c.party }),
    sell,
    upload: o => { opts.setTab('photos'); setUploadReq({ ...(o || {}), n: Date.now() }); },
    openCard: id => navigate(`/treatment/cards?id=${id}`),
    openOrder: setOrderId,
  };

  const count = (label: string, n: number, danger?: boolean) => (n > 0
    ? <Badge count={n} size="small" offset={[6, -2]} color={danger ? '#ef4444' : '#357f96'}><span className="pr-2.5">{label}</span></Badge>
    : label);

  const items = [
    { key: '360', label: 'Hồ sơ 360', children: <Overview360 c={c} ops={ops}>{opts.appointments}</Overview360> },
    { key: 'class', label: 'Phân loại', children: <ClassPanel c={c} /> },
    { key: 'treatment', label: count('Liệu trình', c.metrics.activeCards), children: <TreatmentPanel c={c} ops={ops} onViewDoc={setDocId} /> },
    { key: 'visits', label: count('Lần đến', c.visits.filter(v => v.status === 'pending' || v.status === 'partial').length, c.visits.some(v => v.overdue)), children: <VisitsPanel c={c} ops={ops} onViewDoc={setDocId} /> },
    { key: 'photos', label: count('Ảnh liệu trình', c.data.photos.filter(p => !p.hidden).length), children: <PhotoPanel c={c} ops={ops} uploadReq={uploadReq} onUploadHandled={() => setUploadReq(null)} /> },
    { key: 'sign', label: count('Chữ ký điện tử', c.needs.filter(n => n.kind !== 'visit').length, true), children: <SignPanel c={c} ops={ops} onView={setDocId} /> },
    { key: 'ledger', label: 'Lịch sử giao dịch', children: <LedgerPanel c={c} ops={ops} /> },
  ];

  const badges = customer ? (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <TierBadge tier={c.cls.tier} manual={!!c.cls.override?.tier} />
      <SegmentBadge segment={c.cls.segment} manual={!!c.cls.override?.segment} />
      {c.metrics.overdue > 0 && <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white">Nợ quá hạn</span>}
    </span>
  ) : null;

  const modals = (
    <ConfigProvider theme={posTheme}>
      <SellCardModal open={sellOpen} customer={sellCustomer} onClose={() => setSellOpen(false)}
        onDone={card => { opts.setTab('treatment'); message.info(`Mời khách ký xác nhận mua thẻ ${card.id} (nút "Chưa ký HĐ" trên thẻ)`, 6); }} />
      <PayRequestModal card={cardOf(payId)} onClose={() => setPayId(null)} />
      <DeductModal card={cardOf(deductId)} onClose={() => setDeductId(null)} />
      <BookingModal card={cardOf(bookId)} onClose={() => setBookId(null)} />
      <SignModal target={signTarget} party={c.party} customerKey={c.key} cards={c.data.cards} onClose={() => setSignTarget(null)} />
      <VisitSignModal target={visitTarget} onClose={() => setVisitTarget(null)} />
      <DocViewer id={docId} onClose={() => setDocId(null)} />
      {orderId && (
        <>
          <div className="fixed inset-0 z-[1000] bg-black/30" onClick={() => setOrderId(null)} />
          <aside className="fixed bottom-0 right-0 top-0 z-[1001] w-full max-w-[660px] border-l border-slate-200 bg-white shadow-2xl">
            <OrderDetail orderId={orderId} onClose={() => setOrderId(null)} />
          </aside>
        </>
      )}
    </ConfigProvider>
  );

  /** Ghi nhật ký sửa thông tin khách (bỏ qua dòng không đổi) */
  const logProfile = (title: string, changes: AuditChange[]) => {
    const real = changes.filter(ch => ch.from !== ch.to);
    if (c.key && (real.length || !changes.length)) useC360Store.getState().audit(c.key, { by: userName, kind: 'profile', title, changes: real });
  };
  const history = <HistoryPanel c={c} customerId={customer?.id} />;

  return { items, badges, modals, history, logProfile, c };
}
